import * as React from 'react';
import * as _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Environment } from '../../../../const/environment';
import PopupWrapper from '../../../components/Popup/PopupWrapper';
import PopupHeader from '../../../components/Popup/PopupHeader';
import PopupBody from '../../../components/Popup/PopupBody';
import { IOpGraphData } from '../../../../models/IOpGraphData';
import TextFieldWrapper from '../../../components/TextFieldWrapper/TextFieldWrapper';
import { GraphRenderer } from '../../../components/GraphRenderer/GraphRenderer';
import { IScript } from '../../../../models/IScript';
import scriptGraphService from '../../../../services/ScriptGraphService';
import { useClosePopup } from '../../../../hooks/useClosePopup';
import PopupHeaderRightPart from '../../../components/Popup/PopupHeaderRightPart';
import ToasterService from '../../../../services/ToasterService';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';

type IOpGraphDataWithCacheProps = { svg: SVGElement, engine: string } & IOpGraphData;

const OPEN_GRAPH_EMGINES = [
    { id: 'fdp', label: 'fdp' },
    { id: 'sfdp', label: 'sfdp' },
    { id: 'dot', label: 'dot' },
    { id: 'neato', label: 'neato' },
    { id: 'circo', label: 'circo' },
    { id: 'osage', label: 'osage' },
    { id: 'twopi', label: 'twopi' },
    { id: 'patchwork', label: 'patchwork' },
];

const searchByScriptGraph = async (script, query): Promise<[string[] | null, string | null]> => {

    return new Promise((resolve, reject) => {
        const param = { id: script.scriptId, names: [script.name], query };

        return window.tutorProject.call('searchByScriptGraph', param, function (err, res) {

            if (err) {
                resolve([null, err]);
            }

            if (res?.nodeIds?.length > 0) {
                resolve([res?.nodeIds, null]);
            }

            resolve([null, 'Unexpected search response']);
        });
    });
}

async function getOpGraphDataFromLog(script: IScript) {

    console.time('get graph data');

    return new Promise((resolve, reject) => {
        const param = { id: script.scriptId, names: [script.name] };

        console.time('getGraphSvg')
        return window.tutorProject.call('getScriptDiagram2', param, function (err, res) {
            console.timeEnd('get graph data');
            if (err) {
                reject();
            }

            console.timeEnd('getGraphSvg')

            if (res?.url) {
                fetch(res?.url)
                    .then(response => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }
                        return response.text();
                    })
                    .then(svgText => {
                        if (!svgText) {
                            reject('empty-svg');
                        }
                        console.time('parseGraphSvg')
                        const parser = new DOMParser();
                        const svgDocument = parser.parseFromString(svgText, 'image/svg+xml');
                        const svgElement = svgDocument.documentElement;
                        console.timeEnd('parseGraphSvg')
                        resolve({
                            svg: svgElement,
                            dot: ''
                        });
                    })
                    .catch(error => {
                        console.error('Error fetching or parsing the SVG:', error);
                    });

            }
        });
    });
}

type ScriptGraphPopupProps = {
    onClose: () => void,
    environment: Environment
    projectId: string
    script: IScript
}

function ScriptGraphPopup({ script, environment, onClose, projectId }: ScriptGraphPopupProps) {

    const [graph, setGraph] = useState(undefined);
    const [title, setTitle] = useState('');
    const [error, setError] = useState(null);
    const [selectedEngine, setSelectedEngine] = useState(OPEN_GRAPH_EMGINES[0].id);
    const [svg, setSvg] = useState<SVGElement>();
    const [actionsMap, setActionsMap] = useState({});
    const [isSearchLoaderShown, setIsSearchLoaderShown] = useState(false);

    const graphTitle = script?.name || 'Script Diagram';

    const [query, setQuery] = useState('');
    const [enabledNodes, setDisabledNodes] = useState<string[]>([]);
    const lastQuery = useRef();
    const searchIsActiveForQuery = useRef(null);

    useClosePopup(onClose);

    useEffect(() => {
        setError(null);
        getOpGraphDataFromLog(script).then((res: IOpGraphDataWithCacheProps) => {

            console.log(res);

            if ((window as any).fakeOpGraphResult) {
                res = (window as any).fakeOpGraphResult;
            }
            if (res?.dot !== undefined || res?.svg !== undefined) {
                setGraph(res.dot);
                setActionsMap(res.val);
                setTitle(res.title || graphTitle);
                if (res.svg) {
                    setSvg(res.svg);
                } if (res.engine) {
                    setSelectedEngine(res.engine);
                }
            } else {
                setGraph(null);
                setTitle(graphTitle);
            }
        }).catch((err) => {
            setGraph(null);
            if (err === 'empty-svg') {
                setError('Script diagram is not available for this script');
            }
        });
    }, [script]);

    const handlePanZoomChange = useCallback((zoomData, panData) => {
        console.info('zoomData, panData', zoomData, panData);
        scriptGraphService.setZoom(script.scriptId, zoomData);
        scriptGraphService.setPan(script.scriptId, panData);
    }, []);

    const searchByNodes = useCallback(async (newQuery) => {

        setIsSearchLoaderShown(true);
        searchIsActiveForQuery.current = newQuery;

        const [nodes, error] = await searchByScriptGraph(script, newQuery);

        if (searchIsActiveForQuery.current !== newQuery) {
            return;
        }

        if (nodes) {
            setDisabledNodes(nodes);
        } else if (error) {
            ToasterService.showError({ title: 'Something went wrong during a search' });
        }
        setIsSearchLoaderShown(false);
    }, [script]);

    const debouncedSearch = useCallback(_.debounce((newQuery) => {
        const trimmedQuery = newQuery.trim();

        if (
            lastQuery?.current !== trimmedQuery
        ) {

            lastQuery.current = trimmedQuery;

            if (trimmedQuery !== '') {
                searchByNodes(newQuery);
            }
        }
    }, 2000), [searchByNodes]);


    const handleQueryChange = useCallback((e) => {
        const newQuery = e.target.value;
        const trimmedQuery = newQuery.trim();

        if (trimmedQuery !== '') {
            setQuery(newQuery);
            debouncedSearch(newQuery);
        } else {
            handleQueryClose();
        }
    }, [debouncedSearch]);

    const handleQueryClose = useCallback(() => {
        setQuery('');
        setDisabledNodes([]);
        setIsSearchLoaderShown(false);
        searchIsActiveForQuery.current = null;
    }, []);

    return <PopupWrapper className="script-graph-popup">
        <PopupHeader
            title={title || ''}
            onCancel={onClose}>

            {/* <PopupHeaderRightPart>
                <div className="script-graph-popup__top-ctrls">
                    <div className="script-graph-popup__search-wrapper">
                        <i className="icon icon-alan-search" />
                        <TextFieldWrapper
                            name="query"
                            id="query"
                            placeholder="Search"
                            value={query}
                            autoComplete="off"
                            onChange={handleQueryChange}
                            onBlur={() => { }}
                            onEsc={handleQueryClose}
                            skipOnChangeAfterOnBlur={true}
                            height={30}
                        />
                        {query && <i className="icon icon-alan-close-rounded" onClick={handleQueryClose} />}
                    </div>
                </div>
            </PopupHeaderRightPart> */}
        </PopupHeader>
        <PopupBody>
            <GraphRenderer
                graph={graph}
                projectId={projectId}
                engine={selectedEngine}
                environment={environment}
                actionsMap={actionsMap}
                svg={svg}
                error={error}
                onClose={onClose}
                enabledNodes={enabledNodes}
                predefinedZoom={scriptGraphService.getZoom(script.scriptId)}
                predefinedPan={scriptGraphService.getPan(script.scriptId)}
                onPanZoomChange={handlePanZoomChange}
            />
            {isSearchLoaderShown &&
                <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} position="absolute" center={true} />}
        </PopupBody>
    </PopupWrapper>;
}

export default ScriptGraphPopup;